import {
  useCombobox,
  UseComboboxPropGetters,
  UseComboboxSelectedItemChange,
  useSelect,
  UseSelectPropGetters,
  UseSelectSelectedItemChange,
} from "downshift";
import type { ReactNode } from "react";
import styles from "./SelectBox.module.css";
import { Icon } from "@/ui-kit";
import Colors from "ui-kit/colors";
import classnames from "classnames";
import { useWebTranslation } from "translations";

function List<T>({
  getMenuProps,
  getItemProps,
  isOpen,
  highlightedIndex,
  items,
  itemToString,
}: {
  getMenuProps:
    | UseSelectPropGetters<T>["getMenuProps"]
    | UseComboboxPropGetters<T>["getMenuProps"];
  getItemProps:
    | UseSelectPropGetters<T>["getItemProps"]
    | UseComboboxPropGetters<T>["getItemProps"];
  isOpen: boolean;
  highlightedIndex: number;
  items: T[];
  itemToString?: (item: T | null) => string;
}) {
  return (
    <ul {...getMenuProps()} className={styles.listContainer}>
      {isOpen && items.length > 0 && (
        <div className={styles.list}>
          {items.map((item, index) => (
            <li
              className={styles.listItem}
              style={{
                backgroundColor:
                  highlightedIndex === index ? Colors.grey200 : undefined,
              }}
              key={`${item}${index}`}
              {...getItemProps({
                item,
                index,
              })}
            >
              {itemToString && itemToString(item)}
            </li>
          ))}
        </div>
      )}
    </ul>
  );
}

export function Select<T>({
  onSelectedItemChange,
  items = [],
  itemToString,
  defaultSelectedItem,
  placeholder,
  icon,
  label,
}: {
  onSelectedItemChange: (
    changes: UseSelectSelectedItemChange<T | null>
  ) => void;
  items: T[];
  itemToString?: (item: T | null) => string;
  defaultSelectedItem?: T | null;
  placeholder?: string;
  icon?: ReactNode;
  label: string;
}) {
  const { t } = useWebTranslation();

  const {
    isOpen,
    getMenuProps,
    getToggleButtonProps,
    getLabelProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
  } = useSelect({
    items,
    itemToString,
    onSelectedItemChange,
    defaultSelectedItem,
  });

  return (
    <div className={styles.container}>
      <label aria-hidden className="sr-only" {...getLabelProps()}>
        {label}
      </label>
      <div className={styles.inputContainer} {...getToggleButtonProps()}>
        {icon ? <div style={{ display: "flex" }}>{icon}</div> : null}
        <input
          className={styles.input}
          placeholder={placeholder}
          value={itemToString && itemToString(selectedItem)}
          style={{ cursor: "pointer" }}
          readOnly
        />
        <button
          className={classnames(styles.button, styles.toggleButton)}
          aria-label={t("a11y.signup.toggle_menu")}
          data-testid="combobox-toggle-button"
        >
          {
            <Icon
              name={isOpen ? "chevron-up" : "chevron-down"}
              size="S"
              color={Colors.grey300}
            />
          }
        </button>
      </div>
      <div className={styles.listWrapper}>
        <List
          getMenuProps={getMenuProps}
          getItemProps={getItemProps}
          isOpen={isOpen}
          highlightedIndex={highlightedIndex}
          items={items}
          itemToString={itemToString}
        />
      </div>
    </div>
  );
}

export function SelectBox<T>({
  onInputValueChange,
  onSelectedItemChange,
  items = [],
  itemToString,
  defaultSelectedItem,
  placeholder,
  showListToggleButton,
  icon,
  allowClear,
  label,
}: {
  onInputValueChange: ({ inputValue }: { inputValue: string }) => void;
  onSelectedItemChange: (
    changes: UseComboboxSelectedItemChange<T | null>
  ) => void;
  items: T[];
  itemToString?: (item: T | null) => string;
  defaultSelectedItem?: T | null;
  placeholder?: string;
  showListToggleButton?: boolean;
  icon?: ReactNode;
  allowClear?: boolean;
  label: string;
}) {
  const { t } = useWebTranslation();

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getToggleButtonProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    selectItem,
    getLabelProps,
  } = useCombobox({
    items,
    onInputValueChange,
    onSelectedItemChange,
    itemToString,
    defaultSelectedItem,
  });

  return (
    <div className={styles.container}>
      <label aria-hidden className="sr-only" {...getLabelProps()}>
        {label}
      </label>
      <div className={styles.inputContainer}>
        {icon ? <div style={{ display: "flex" }}>{icon}</div> : null}
        <input
          className={styles.input}
          {...getInputProps()}
          placeholder={placeholder}
        />
        {allowClear && selectedItem ? (
          <button
            className={classnames(styles.button, styles.clearButton)}
            aria-label={t("global.clear")}
            data-testid="clear-button"
            onClick={() => selectItem(null)}
          >
            <Icon name="error-circle" size="S" color={Colors.grey300} />
          </button>
        ) : null}
        {showListToggleButton ? (
          <button
            className={classnames(styles.button, styles.toggleButton)}
            aria-label={t("a11y.signup.toggle_menu")}
            data-testid="combobox-toggle-button"
            {...getToggleButtonProps()}
          >
            {
              <Icon
                name={isOpen ? "chevron-up" : "chevron-down"}
                size="S"
                color={Colors.grey300}
              />
            }
          </button>
        ) : null}
      </div>
      <List
        getMenuProps={getMenuProps}
        getItemProps={getItemProps}
        isOpen={isOpen}
        highlightedIndex={highlightedIndex}
        items={items}
        itemToString={itemToString}
      />
    </div>
  );
}
